// Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved.

import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, ArrowLeft, User, Search } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  getOrCreateConversation,
  subscribeConversations,
  subscribeMessages,
  sendMessage as sendMessageApi,
  getOtherParticipantId,
  getOtherParticipantIdFromConversationId,
  getUsersDisplayNames
} from '../utils/chatService';

const Chat = ({ setCurrentPage, initialOtherUserId = null }) => {
  const { currentUser, userProfile } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [displayNames, setDisplayNames] = useState({});
  const [openingWithUserId, setOpeningWithUserId] = useState(initialOtherUserId);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [openingChatUserId, setOpeningChatUserId] = useState(null);
  const [chatError, setChatError] = useState(null);
  const searchTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  const listRef = useRef(null);

  const showSearchResults = searchQuery.trim().length > 0;

  // When opening chat with a specific user (e.g. from Profile "Message"), get or create conversation and select it
  useEffect(() => {
    if (!currentUser || !openingWithUserId) return;
    let cancelled = false;
    setChatError(null);
    setOpeningChatUserId(openingWithUserId);
    (async () => {
      try {
        const cid = await getOrCreateConversation(currentUser.uid, openingWithUserId);
        if (!cancelled && cid) {
          setSelectedConversationId(cid);
          setOpeningWithUserId(null);
        }
      } catch (e) {
        console.error('Error opening conversation:', e);
        if (!cancelled) {
          setChatError(e?.message || 'Could not start conversation. Try again.');
          setOpeningWithUserId(null);
        }
      } finally {
        if (!cancelled) setOpeningChatUserId(null);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser, openingWithUserId]);

  // Clear initial "open with user" after we've handled it so we don't re-run
  useEffect(() => {
    if (initialOtherUserId && selectedConversationId) setOpeningWithUserId(null);
  }, [initialOtherUserId, selectedConversationId]);

  // Subscribe to conversations list
  useEffect(() => {
    if (!currentUser) return;
    const unsub = subscribeConversations(currentUser.uid, setConversations);
    return unsub;
  }, [currentUser]);

  // Debounced user search for "find who to message"
  useEffect(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!currentUser || !query) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const usersSnap = await getDocs(collection(db, 'users'));
        const list = usersSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => (u.uid || u.id) !== currentUser.uid)
          .filter((u) => {
            const name = (u.displayName || '').toLowerCase();
            const email = (u.email || '').toLowerCase();
            return name.includes(query) || email.includes(query);
          })
          .slice(0, 30);
        setSearchResults(list);
      } catch (err) {
        console.error('Chat user search error:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, currentUser]);

  // Resolve display names for conversation list
  useEffect(() => {
    const uids = new Set();
    conversations.forEach((c) => {
      (c.participantIds || []).forEach((id) => id !== currentUser?.uid && uids.add(id));
    });
    if (uids.size === 0) {
      setDisplayNames({});
      return;
    }
    getUsersDisplayNames([...uids]).then(setDisplayNames);
  }, [conversations, currentUser?.uid]);

  // When thread is open with a user not yet in list (e.g. just opened from Profile), ensure their name is loaded
  useEffect(() => {
    if (!selectedConversationId || !currentUser?.uid) return;
    const conv = conversations.find((c) => c.id === selectedConversationId);
    const otherId = conv
      ? getOtherParticipantId(conv, currentUser.uid)
      : getOtherParticipantIdFromConversationId(selectedConversationId, currentUser.uid);
    if (!otherId || displayNames[otherId]) return;
    getUsersDisplayNames([otherId]).then((map) =>
      setDisplayNames((prev) => ({ ...prev, ...map }))
    );
  }, [selectedConversationId, currentUser?.uid, conversations, displayNames]);

  // Subscribe to messages for selected conversation
  useEffect(() => {
    if (!selectedConversationId) {
      setMessages([]);
      return;
    }
    const unsub = subscribeMessages(selectedConversationId, setMessages);
    return unsub;
  }, [selectedConversationId]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const selectedConversation = conversations.find((c) => c.id === selectedConversationId);
  const otherUserId = selectedConversation
    ? getOtherParticipantId(selectedConversation, currentUser?.uid)
    : getOtherParticipantIdFromConversationId(selectedConversationId, currentUser?.uid);
  const otherDisplayName = otherUserId ? (displayNames[otherUserId] || 'Angler') : null;

  const handleSend = async () => {
    const text = messageText.trim();
    if (!currentUser || !selectedConversationId || !text || sending) return;
    setSending(true);
    try {
      await sendMessageApi(selectedConversationId, {
        senderId: currentUser.uid,
        senderName: userProfile?.displayName || currentUser.displayName || 'Anonymous',
        text
      });
      setMessageText('');
    } catch (e) {
      console.error('Send message error:', e);
    } finally {
      setSending(false);
    }
  };

  const handleOpenChatWithUser = async (otherUserId) => {
    const uid = typeof otherUserId === 'string' ? otherUserId : (otherUserId && (otherUserId.uid || otherUserId.id));
    if (!currentUser || !uid || uid === currentUser.uid) return;
    setChatError(null);
    setSearchQuery('');
    setSearchResults([]);
    setOpeningChatUserId(uid);
    try {
      const cid = await getOrCreateConversation(currentUser.uid, uid);
      if (cid) setSelectedConversationId(cid);
    } catch (e) {
      console.error('Error opening chat:', e);
      setChatError(e?.message || 'Could not start conversation. Try again.');
    } finally {
      setOpeningChatUserId(null);
    }
  };

  const formatMessageTime = (createdAt) => {
    if (!createdAt) return '';
    const date = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const diffDays = Math.floor((now - date) / (24 * 60 * 60 * 1000));
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'short' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatLastMessageTime = (updatedAt) => {
    if (!updatedAt) return '';
    const date = updatedAt?.toDate ? updatedAt.toDate() : new Date(updatedAt);
    const now = new Date();
    const diffMs = now - date;
    if (diffMs < 60000) return 'Just now';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m`;
    if (date.toDateString() === now.toDateString()) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (diffMs < 86400000 * 2) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const currentUserPhoto = userProfile?.photoURL || currentUser?.photoURL;

  return (
    <div className="chat-page">
      <div className="chat-layout">
        {/* Conversation list (Instagram-style inbox list) */}
        <aside className={`chat-sidebar ${selectedConversationId ? 'chat-sidebar-hidden' : ''}`}>
          <div className="chat-sidebar-header">
            <h2 className="chat-sidebar-title">
              <MessageCircle size={24} />
              Messages
            </h2>
            <div className="chat-search-wrap">
              <Search size={18} className="chat-search-icon" aria-hidden />
              <input
                type="text"
                className="chat-search-input"
                placeholder="Search to message..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search for someone to message"
              />
            </div>
          </div>
          <div className="chat-conversation-list" ref={listRef}>
            {showSearchResults ? (
              <>
                {searchLoading ? (
                  <div className="chat-search-loading">Searching...</div>
                ) : searchResults.length === 0 ? (
                  <div className="chat-empty-inbox">
                    <Search size={40} strokeWidth={1.2} />
                    <p>No users found</p>
                    <p className="chat-empty-hint">Try a different name or email.</p>
                  </div>
                ) : (
                  <div className="chat-search-results">
                    {chatError && (
                      <div className="chat-error-message" role="alert">
                        {chatError}
                        <button type="button" className="chat-error-dismiss" onClick={() => setChatError(null)} aria-label="Dismiss">×</button>
                      </div>
                    )}
                    {searchResults.map((user) => {
                      const targetUid = user.uid || user.id;
                      const isOpening = openingChatUserId === targetUid;
                      return (
                      <button
                        key={targetUid}
                        type="button"
                        className="chat-conversation-item"
                        onClick={() => handleOpenChatWithUser(targetUid)}
                        disabled={!!openingChatUserId}
                      >
                        <div className="chat-conversation-avatar">
                          <User size={20} />
                        </div>
                        <div className="chat-conversation-body">
                          <div className="chat-conversation-row">
                            <span className="chat-conversation-name">
                              {user.displayName || 'Angler'}
                              {isOpening && <span className="chat-opening-label"> Opening…</span>}
                            </span>
                          </div>
                        </div>
                      </button>
                    );})}
                  </div>
                )}
              </>
            ) : conversations.length === 0 ? (
              <div className="chat-empty-inbox">
                <MessageCircle size={48} strokeWidth={1.2} />
                <p>No conversations yet</p>
                <p className="chat-empty-hint">Message anglers from Community or their profile to start a chat.</p>
              </div>
            ) : (
              conversations.map((conv) => {
                const otherId = getOtherParticipantId(conv, currentUser?.uid);
                const name = otherId ? (displayNames[otherId] || 'Angler') : 'Angler';
                const last = conv.lastMessage;
                const preview = last?.text || 'No messages yet';
                const isSelected = conv.id === selectedConversationId;
                return (
                  <button
                    key={conv.id}
                    type="button"
                    className={`chat-conversation-item ${isSelected ? 'active' : ''}`}
                    onClick={() => setSelectedConversationId(conv.id)}
                  >
                    <div className="chat-conversation-avatar">
                      <User size={20} />
                    </div>
                    <div className="chat-conversation-body">
                      <div className="chat-conversation-row">
                        <span className="chat-conversation-name">{name}</span>
                        <span className="chat-conversation-time">{formatLastMessageTime(conv.updatedAt)}</span>
                      </div>
                      <div className="chat-conversation-preview">{preview}</div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* Thread view */}
        <main className={`chat-thread ${selectedConversationId ? 'chat-thread-visible' : ''}`}>
          {!selectedConversationId ? (
            <div className="chat-thread-placeholder">
              <MessageCircle size={64} strokeWidth={1} />
              <p>Select a conversation or search above to message someone</p>
            </div>
          ) : (
            <>
              <div className="chat-thread-header">
                <button
                  type="button"
                  className="chat-thread-back"
                  onClick={() => setSelectedConversationId(null)}
                  aria-label="Back to conversations"
                >
                  <ArrowLeft size={22} />
                </button>
                <div className="chat-thread-header-avatar">
                  <User size={20} />
                </div>
                <span className="chat-thread-header-name">{otherDisplayName || 'Angler'}</span>
              </div>

              <div className="chat-messages">
                {messages.map((msg) => {
                  const isOwn = msg.senderId === currentUser?.uid;
                  const createdAt = msg.createdAt?.toDate ? msg.createdAt.toDate() : new Date(msg.createdAt);
                  return (
                    <div
                      key={msg.id}
                      className={`chat-message-wrapper ${isOwn ? 'chat-message-own' : ''}`}
                    >
                      <div className="chat-message-bubble">
                        {!isOwn && (
                          <span className="chat-message-sender">{msg.senderName}</span>
                        )}
                        <span className="chat-message-text">{msg.text}</span>
                        <span className="chat-message-time">{formatMessageTime(msg.createdAt)}</span>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <div className="chat-input-area">
                <input
                  type="text"
                  className="chat-input"
                  placeholder="Message..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  maxLength={2000}
                />
                <button
                  type="button"
                  className="chat-send-btn"
                  onClick={handleSend}
                  disabled={!messageText.trim() || sending}
                  aria-label="Send message"
                >
                  <Send size={20} />
                </button>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default Chat;
